import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import {
  PRIO,
  type PublicScreenDto,
  type ScreenSettingsDto,
  type ScreenTheme,
  type ScreenVisitDto,
  type WeatherKind,
} from "@klantpunt/shared";
import { BrandMark } from "../app/BrandMark";
import { Meta } from "../app/Meta";
import {
  IconAlert,
  IconCheck,
  IconCloud,
  IconDrizzle,
  IconFog,
  IconInbox,
  IconInspections,
  IconMonitor,
  IconPartlySunny,
  IconPlanning,
  IconRain,
  IconSnow,
  IconStorm,
  IconSun,
  IconTasks,
} from "../app/icons";
import { api, ApiError } from "../lib/api";
import { forgetScreenToken } from "./ScreenPair";

const REFRESH_MS = 60_000;

const WEATHER_ICONS: Record<WeatherKind, typeof IconSun> = {
  clear: IconSun,
  partly: IconPartlySunny,
  cloudy: IconCloud,
  fog: IconFog,
  drizzle: IconDrizzle,
  rain: IconRain,
  snow: IconSnow,
  storm: IconStorm,
};

const KIND_LABELS: Record<ScreenVisitDto["kind"], string> = {
  keuring: "Keuring",
  onderhoud: "Onderhoud",
  taak: "Taak",
  meter: "Meterstanden",
};

const time = (d: Date) => d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
const day = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short" });

/** "Sinds vandaag", "Sinds gisteren", "Al 3 dagen". */
function since(iso: string, today: string) {
  const d = new Date(iso).toLocaleDateString("sv-SE", { timeZone: "Europe/Amsterdam" });
  const days = Math.round((new Date(today).getTime() - new Date(d).getTime()) / 86_400_000);
  return days <= 0 ? "Sinds vandaag" : days === 1 ? "Sinds gisteren" : `Al ${days} dagen`;
}

/** Alleen bij een afspraak van meer dagen; een losse dag staat al onder Vandaag. */
function visitWhen(v: ScreenVisitDto, today: string) {
  if (v.startOn === v.endOn) return null;
  if (v.endOn === today) return "Laatste dag";
  return `t/m ${day(v.endOn)}`;
}

/** Klok die elke 10 seconden bijwerkt (de minuut springt op tijd om). */
export function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 10_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/** Scherm niet laten slapen zolang de pagina zichtbaar is (waar de browser dat kan). */
export function useWakeLock() {
  useEffect(() => {
    const nav = navigator as Navigator & {
      wakeLock?: { request: (t: "screen") => Promise<{ release: () => Promise<void> }> };
    };
    if (!nav.wakeLock) return;
    let lock: { release: () => Promise<void> } | null = null;
    const take = () => {
      if (document.visibilityState === "visible")
        void nav.wakeLock!.request("screen").then(
          (l) => (lock = l),
          () => undefined,
        );
    };
    take();
    document.addEventListener("visibilitychange", take);
    return () => {
      document.removeEventListener("visibilitychange", take);
      void lock?.release().catch(() => undefined);
    };
  }, []);
}

type Tone = "accent" | "ok" | "warn" | "danger";

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/** Telt rustig op naar een nieuw getal (0,7 s), zoals de kerncijfers in het Overzicht. */
function useCountUp(value: number) {
  const [shown, setShown] = useState(reducedMotion() ? value : 0);
  const from = useRef(shown);
  useEffect(() => {
    const start = from.current;
    if (start === value || reducedMotion()) {
      from.current = value;
      setShown(value);
      return;
    }
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / 700);
      const v = Math.round(start + (value - start) * (1 - (1 - k) ** 3));
      from.current = v;
      setShown(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return shown;
}

/** Sleutels die er bij de vorige verversing nog niet waren: die regels lichten even op. */
function useNewKeys(keys: string[] | null) {
  const seen = useRef<Set<string> | null>(null);
  const joined = keys?.join("|") ?? null;
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  useEffect(() => {
    // Pas vanaf de eerste gegevens vergelijken: bij het openen licht er niets op.
    if (joined === null) return;
    const now = new Set(joined ? joined.split("|") : []);
    if (seen.current) setFresh(new Set([...now].filter((k) => !seen.current!.has(k))));
    seen.current = now;
  }, [joined]);
  return fresh;
}

function Num({ value }: { value: number }) {
  return <>{useCountUp(value)}</>;
}

/** Kaart op het scherm, in de stijl van de tegels in het Overzicht: icoon in de kleur van de toon, zachte gloed. */
function Card({
  icon,
  title,
  count,
  aside,
  tone = "accent",
  className = "",
  children,
}: {
  icon: ReactNode;
  title: string;
  count?: number;
  /** Korte tekst rechts in de kop, in plaats van een aantal. */
  aside?: ReactNode;
  tone?: Tone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`scr-card tone-${tone} ${className}`.trim()} aria-label={title}>
      <h2 className="scr-h">
        <span className="scr-ico" aria-hidden="true">
          {icon}
        </span>
        <span className="grow">{title}</span>
        {count !== undefined && (
          <span className="scr-count">
            <Num value={count} />
          </span>
        )}
        {aside && <span className="scr-meta">{aside}</span>}
      </h2>
      {children}
    </section>
  );
}

const PAGE_MS = 10_000;

/**
 * Lijst die altijd in de kaart past: wat er niet op past, komt op een volgende pagina die na 10 seconden
 * inschuift (met puntjes onderaan). Op de telefoon groeit de lijst gewoon mee.
 */
function Paged({ keys, children }: { keys: string[]; children: ReactNode }) {
  const view = useRef<HTMLDivElement>(null);
  const [starts, setStarts] = useState<number[]>([0]);
  const [page, setPage] = useState(0);
  const [cramped, setCramped] = useState(false);
  const joined = keys.join("|");
  useLayoutEffect(() => {
    const el = view.current;
    if (!el) return;
    const measure = () => {
      const rows = [...el.querySelectorAll<HTMLElement>(".scr-item")];
      const h = el.clientHeight;
      const next = [0];
      let top = rows[0]?.offsetTop ?? 0;
      rows.forEach((r, i) => {
        if (i > next[next.length - 1]! && r.offsetTop + r.offsetHeight - top > h) {
          next.push(i);
          top = r.offsetTop;
        }
      });
      setStarts((old) => (old.join() === next.join() ? old : next));
      // Past zelfs de eerste regel niet (lage kaart naast de cijfers), dan liever niets dan een half vak.
      setCramped(rows.length > 0 && rows[0]!.offsetHeight > h);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [joined]);
  useEffect(() => {
    setPage((p) => (p < starts.length ? p : 0));
    if (starts.length < 2) return;
    const t = setInterval(() => setPage((p) => (p + 1) % starts.length), PAGE_MS);
    return () => clearInterval(t);
  }, [starts]);
  useLayoutEffect(() => {
    const el = view.current;
    if (!el) return;
    const rows = [...el.querySelectorAll<HTMLElement>(".scr-item")];
    const from = starts[page] ?? 0;
    const to = starts[page + 1] ?? rows.length;
    rows.forEach((r, i) => r.toggleAttribute("data-off", i < from || i >= to));
    const list = el.firstElementChild as HTMLElement | null;
    if (list) list.style.transform = `translateY(${(rows[0]?.offsetTop ?? 0) - (rows[from]?.offsetTop ?? 0)}px)`;
  });
  return (
    <div className={cramped ? "scr-pager is-cramped" : "scr-pager"}>
      <div className="scr-view" ref={view}>
        {children}
      </div>
      <div className="scr-pages" aria-hidden="true">
        {starts.length > 1 &&
          starts.map((_, i) => (
            <i key={i} className={i === page ? "on" : ""} style={{ animationDuration: `${PAGE_MS}ms` }} />
          ))}
      </div>
    </div>
  );
}

/**
 * Thema van het scherm: de instelling bij Gebouw › Schermen (standaard donker); `?thema=licht` of `?thema=donker`
 * achter de link gaat voor.
 */
export function useScreenTheme(setting: ScreenTheme = "dark") {
  useEffect(() => {
    const el = document.documentElement;
    const before = el.dataset.theme;
    const force = new URLSearchParams(location.search).get("thema");
    el.dataset.theme = force === "licht" ? "light" : force === "donker" ? "dark" : setting;
    return () => {
      if (before) el.dataset.theme = before;
      else delete el.dataset.theme;
    };
  }, [setting]);
}

/**
 * Nachtstand volgens de instelling bij het scherm (standaard 19:00 tot 7:00, null = uit); `?nacht=aan` of
 * `?nacht=uit` achter de link gaat voor.
 */
function isNight(now: Date, night: ScreenSettingsDto["night"]): boolean {
  const force = new URLSearchParams(window.location.search).get("nacht");
  if (force === "aan") return true;
  if (force === "uit") return false;
  if (!night) return false;
  const h = now.getHours();
  return night.from > night.until ? h >= night.from || h < night.until : h >= night.from && h < night.until;
}

/**
 * Gebouwdashboard op een scherm (/s/<token>, docs/SCHERMEN.md): alleen lezen, zonder inloggen, ververst zichzelf
 * elke minuut. Bij een storing blijft de laatste stand staan met de tijd van bijwerken.
 */
export function ScreenPage() {
  const { token = "" } = useParams();
  const now = useNow();
  useWakeLock();
  const q = useQuery({
    queryKey: ["screen-public", token],
    queryFn: () => api<PublicScreenDto>(`/public/screen/${encodeURIComponent(token)}`),
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: true,
    retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 3,
  });
  useScreenTheme(q.data?.settings.theme);
  const [full, setFull] = useState(false);
  useEffect(() => {
    const on = () => setFull(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);
  useEffect(() => {
    const d = q.data;
    document.title = d ? `${d.building.name} · Klantpunt` : "Scherm · Klantpunt";
  }, [q.data]);

  const s0 = q.data;
  const visitKey = (v: ScreenVisitDto) => `${v.kind}:${v.title}:${v.startOn}`;
  const fresh = useNewKeys(
    s0
      ? [
          ...s0.visits.map(visitKey),
          ...s0.issues.map((i) => i.id),
          ...(s0.tasks?.map((t) => t.id) ?? []),
          ...(s0.reports.latest?.map((r) => r.id) ?? []),
        ]
      : null,
  );
  const cls = (key: string) => (fresh.has(key) ? "scr-item is-new" : "scr-item");

  const gone = q.error instanceof ApiError && q.error.status === 404;
  const s = gone ? undefined : s0;
  if (!s)
    return (
      <div className="scr scr-empty">
        <BrandMark size={56} />
        {q.isPending ? (
          <p role="status">Scherm laden…</p>
        ) : (
          <>
            <h1>{gone ? "Dit scherm is niet meer gekoppeld" : "Geen verbinding"}</h1>
            <p className="muted">
              {gone ? "Vraag de beheerder van het gebouw om een nieuwe link." : "We proberen het elke minuut opnieuw."}
            </p>
            {gone && (
              <Link className="btn" to="/scherm" onClick={forgetScreenToken}>
                Opnieuw koppelen met een code
              </Link>
            )}
          </>
        )}
      </div>
    );

  const beheer = s.profile === "beheer";
  const stale = q.isError || Date.now() - q.dataUpdatedAt > 3 * REFRESH_MS;
  const w = s.weather;
  const WIcon = w ? WEATHER_ICONS[w.kind] : IconCloud;
  const rainMax = Math.max(1, ...(w?.rain ?? []));
  const rainSoon = w?.rain.some((r) => r > 0);
  const rainHours = w ? (w.rain.length * w.rainMinutes) / 60 : 0;
  const urgent = s.issues.some((i) => i.priority === "P1" || i.priority === "P2");
  const lateTasks = s.tasks?.some((t) => t.dueOn < s.today);
  const [hh, mm] = time(now).split(":");
  const dateText = now.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
  const banners = s.announcements.length > 0 && (
    <div className="scr-banners">
      {s.announcements.map((a) => (
        <div key={a.id} className="scr-banner" role="note">
          <IconAlert aria-hidden="true" />
          <span>{a.text}</span>
          <span className="scr-meta">{a.endsOn === s.today ? "Vandaag" : `t/m ${day(a.endsOn)}`}</span>
        </div>
      ))}
    </div>
  );

  if (isNight(now, s.settings.night)) {
    // Elke minuut een paar pixels verschuiven, zodat er op een oled-scherm niets inbrandt.
    const m = now.getHours() * 60 + now.getMinutes();
    const drift = { transform: `translate(${((m * 7) % 9) - 4}vw, ${((m * 5) % 7) - 3}vh)` };
    return (
      <div className="scr scr-night" data-profile={s.profile}>
        <div className="scr-night-body" style={drift}>
          <div className="scr-time">
            {hh}
            <span className="scr-colon">:</span>
            {mm}
          </div>
          <div className="scr-night-date">{dateText}</div>
          <p className="scr-night-name">{s.building.name}</p>
          {banners}
        </div>
      </div>
    );
  }

  return (
    <div className={`scr ${w ? "" : "no-weather"}`.trim()} data-profile={s.profile}>
      <header className="scr-top">
        <div className="scr-title">
          <p className="scr-eyebrow">{s.org}</p>
          <h1 className="dot">{s.building.name}</h1>
          {s.building.address && <p className="muted">{s.building.address}</p>}
        </div>
        <div className="scr-clock">
          <div className="scr-time">
            {hh}
            <span className="scr-colon">:</span>
            {mm}
          </div>
          <div className="muted">{dateText}</div>
        </div>
      </header>

      {banners}

      <div className="scr-grid">
        <Card
          className="c-visits"
          icon={<IconPlanning />}
          title="Vandaag verwacht"
          count={s.visits.length}
          tone={s.visits.some((v) => !v.confirmed) ? "warn" : "accent"}
        >
          {s.visits.length ? (
            <Paged keys={s.visits.map(visitKey)}>
              <ul className="scr-list">
                {s.visits.map((v) => (
                  <li key={visitKey(v)} className={cls(visitKey(v))}>
                    <span className="scr-body">
                      <span className="scr-lead">{KIND_LABELS[v.kind]}</span>
                      <span className="scr-ttl">{v.title}</span>
                      <span className="scr-meta">
                        <Meta items={[v.who, visitWhen(v, s.today)]} />
                      </span>
                    </span>
                    <span className={`chip tone ${v.confirmed ? "tone-ok" : "tone-warn"}`}>
                      {v.confirmed ? "Bevestigd" : "Nog niet bevestigd"}
                    </span>
                  </li>
                ))}
              </ul>
            </Paged>
          ) : (
            <p className="scr-none">Vandaag wordt er niemand verwacht.</p>
          )}
        </Card>

        <Card
          className="c-issues"
          icon={<IconAlert />}
          title="Belangrijk"
          count={s.issues.length}
          tone={urgent ? "danger" : s.issues.length ? "warn" : "ok"}
        >
          {s.issues.length ? (
            <Paged keys={s.issues.map((i) => i.id)}>
              <ul className="scr-list">
                {s.issues.map((i) => (
                  <li key={i.id} className={cls(i.id)}>
                    <span className={`scr-dot prio-${i.priority}`} aria-hidden="true" />
                    <span className="scr-body">
                      <span className="scr-ttl">{i.title}</span>
                      <span className="scr-meta">
                        <Meta items={[since(i.since, s.today), i.inProgress && "monteur is ingeschakeld"]} />
                      </span>
                    </span>
                    <span className={`chip prio prio-${i.priority}`}>{PRIO[i.priority].l}</span>
                  </li>
                ))}
              </ul>
            </Paged>
          ) : (
            <p className="scr-none">
              <IconCheck aria-hidden="true" /> Geen storingen. Alles werkt zoals het hoort.
            </p>
          )}
        </Card>

        {beheer && s.tasks && (
          <Card
            className="c-tasks"
            icon={<IconTasks />}
            title="Taken voor vandaag"
            count={s.tasks.length}
            tone={lateTasks ? "warn" : "accent"}
          >
            {s.tasks.length ? (
              <Paged keys={s.tasks.map((t) => t.id)}>
                <ul className="scr-list">
                  {s.tasks.map((t) => (
                    <li key={t.id} className={cls(t.id)}>
                      <span className="scr-body">
                        <span className="scr-lead code">{t.code}</span>
                        <span className="scr-ttl">{t.title}</span>
                        <span className="scr-meta">
                          <Meta items={[t.who, t.dueOn < s.today ? `te laat, was ${day(t.dueOn)}` : "vandaag"]} />
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </Paged>
            ) : (
              <p className="scr-none">Geen taken voor vandaag.</p>
            )}
          </Card>
        )}

        {beheer && s.overdue && (
          <Card
            className="c-overdue"
            icon={<IconInspections />}
            title="Onderhoud en keuringen verlopen"
            count={s.overdue.length}
            tone={s.overdue.length ? "danger" : "ok"}
          >
            {s.overdue.length ? (
              <Paged keys={s.overdue.map((o) => o.id)}>
                <ul className="scr-list">
                  {s.overdue.map((o) => (
                    <li key={o.id} className="scr-item">
                      <span className="scr-body">
                        <span className="scr-ttl">{o.title}</span>
                        <span className="scr-meta">
                          <Meta items={[o.asset, `vervaldatum ${day(o.dueOn)}`]} />
                        </span>
                      </span>
                      <span className="chip tone tone-warn">Verlopen</span>
                    </li>
                  ))}
                </ul>
              </Paged>
            ) : (
              <p className="scr-none">
                <IconCheck aria-hidden="true" /> Alle keuringen zijn op tijd.
              </p>
            )}
          </Card>
        )}

        <Card
          className="c-reports"
          icon={<IconInbox />}
          title="Meldingen"
          tone={beheer && s.reports.toReview ? "warn" : "accent"}
        >
          <div className="scr-kpis">
            {beheer ? (
              <>
                <div className={`scr-kpi ${s.reports.toReview ? "bad" : ""}`.trim()}>
                  <span className="v">
                    <Num value={s.reports.toReview ?? 0} />
                  </span>
                  <span className="l">Te beoordelen</span>
                </div>
                <div className="scr-kpi">
                  <span className="v">
                    <Num value={s.reports.openIssues ?? 0} />
                  </span>
                  <span className="l">Open bevindingen</span>
                </div>
              </>
            ) : (
              <div className="scr-kpi">
                <span className="v">
                  <Num value={s.reports.today} />
                </span>
                <span className="l">Vandaag gemeld</span>
              </div>
            )}
            <div className="scr-kpi good">
              <span className="v">
                <Num value={s.reports.resolvedThisMonth} />
              </span>
              <span className="l">Opgelost deze maand</span>
            </div>
          </div>
          {beheer && s.reports.latest && s.reports.latest.length > 0 ? (
            <Paged keys={s.reports.latest.map((r) => r.id)}>
              <ul className="scr-list">
                {s.reports.latest.map((r) => (
                  <li key={r.id} className={cls(r.id)}>
                    <span className="scr-body">
                      <span className="scr-ttl">{r.title}</span>
                      <span className="scr-meta">
                        <Meta items={[`via ${r.channel}`, time(new Date(r.createdAt))]} />
                      </span>
                    </span>
                    <span className="chip tone tone-warn">Nieuw</span>
                  </li>
                ))}
              </ul>
            </Paged>
          ) : (
            !beheer && (
              <p className="scr-hint">
                Zie je iets kapot? Meld het bij de beheerder of scan de QR-code op de installatie.
              </p>
            )
          )}
        </Card>

        {w && (
          <Card
            className="c-weather"
            icon={<WIcon />}
            title="Weer"
            aside={`${rainSoon ? "Regen" : "Droog"} komende ${rainHours} uur`}
          >
            <div className="scr-now" aria-label={`${w.text}, ${w.temperature} graden`}>
              <span className="scr-temp">
                <Num value={w.temperature} />°
              </span>
              <span className="muted">
                {w.text}
                <br />
                wind {w.windBft} {w.windDir}
              </span>
            </div>
            <div
              className="scr-rain"
              role="img"
              aria-label={`Neerslag per ${w.rainMinutes === 15 ? "kwartier" : "uur"}, komende ${rainHours} uur`}
            >
              {w.rain.map((r, i) => (
                <i
                  key={i}
                  style={{ height: `${Math.max(4, (r / rainMax) * 100)}%`, animationDelay: `${i * 40}ms` }}
                  className={r ? "" : "dry"}
                />
              ))}
            </div>
            <div className="scr-rainlbl">
              <span>nu</span>
              <span>+{rainHours / 2} uur</span>
              <span>+{rainHours} uur</span>
            </div>
            {w.hours.length > 0 && (
              <div className="scr-hours">
                {w.hours.map((h) => {
                  const H = WEATHER_ICONS[h.kind];
                  return (
                    <div key={h.time}>
                      <span className="muted">{h.time}</span>
                      <H aria-hidden="true" />
                      <strong>{h.temperature}°</strong>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        )}
      </div>

      <footer className="scr-foot">
        <BrandMark size={20} title="Klantpunt" />
        <span className={`scr-live ${stale ? "stale" : ""}`.trim()} aria-hidden="true" />
        <span className="muted">
          {stale ? "Geen verbinding, laatst bijgewerkt" : "Live, bijgewerkt"} {time(new Date(q.dataUpdatedAt))}
          {w && ` · weer: ${w.source}`}
        </span>
        <span className="grow" />
        {!full && document.fullscreenEnabled && (
          <button
            type="button"
            className="btn small ghost"
            onClick={() => void document.documentElement.requestFullscreen().catch(() => undefined)}
          >
            <IconMonitor size={16} aria-hidden="true" /> Volledig scherm
          </button>
        )}
      </footer>
    </div>
  );
}
